import { useState } from 'react';
import { tokenManager } from '../config/authConfig';
import API_CIFRA from '../config/Variables_apis';

/**
 * Lectura/edición de la configuración de esta instancia desde el panel
 * (HU-005 Fase 4) — exclusivo de personal `ADMINISTRADOR`/`SUPERADMIN`,
 * gateado por `RutaProtegidaPanel` en el panel, no acá. A diferencia de
 * `useConfiguracion` (público, para el mapa ciudadano), este hook siempre
 * manda el JWT del personal logueado y no tiene un valor por defecto — si
 * falla, la pantalla de administración debe mostrar el error, no ocultarlo
 * detrás de un fallback silencioso.
 */
const useConfiguracionAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = (extra = {}) => ({
    Authorization: `Bearer ${tokenManager.getAccessToken()}`,
    ...extra,
  });

  const manejarRespuesta = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const erroresDeCampo = Object.entries(data)
        .filter(([campo]) => campo !== 'error' && campo !== 'detail')
        .map(([campo, mensajes]) => `${campo}: ${Array.isArray(mensajes) ? mensajes.join(', ') : mensajes}`)
        .join(' — ');
      throw new Error(data.error || data.detail || erroresDeCampo || 'Error inesperado del servidor');
    }
    return data;
  };

  const obtenerConfiguracion = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.configuracion.admin, { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      return { success: true, configuracion: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Campos de imagen (`ImageField` en el backend) — nunca viajan como el
  // valor que ya trae `config` del GET (una URL absoluta), porque el
  // backend los rechazaría como archivo inválido. Solo viajan cuando son un
  // `File` nuevo (subida) o `null` explícito (HU-015: "Quitar banner").
  const CAMPOS_IMAGEN = ['logo', 'banner', 'encabezado_imagen', 'inicio_imagen', 'inicio_fondo_imagen', 'inicio_features_imagen', 'og_imagen', 'og_imagen_2'];

  const actualizarConfiguracion = async (datos) => {
    setLoading(true);
    setError(null);
    try {
      const tieneArchivoNuevo = CAMPOS_IMAGEN.some((campo) => datos[campo] instanceof File);
      let body;
      let headers;
      if (tieneArchivoNuevo) {
        const formData = new FormData();
        Object.entries(datos).forEach(([campo, valor]) => {
          // Una imagen sin cambios llega como URL (string) — no se reenvía,
          // solo la que de verdad es un archivo nuevo.
          if (CAMPOS_IMAGEN.includes(campo) && !(valor instanceof File)) return;
          if (valor === null || valor === undefined) return;
          // Objetos (ej. `reporte_textos`) van como JSON string dentro del FormData.
          formData.append(campo, typeof valor === 'object' && !(valor instanceof File) ? JSON.stringify(valor) : valor);
        });
        body = formData;
        headers = authHeaders();
      } else {
        // Sin archivo nuevo: JSON plano. Un campo de imagen en `null`
        // (quitada a propósito) SÍ viaja — es la señal que el backend
        // necesita para borrarla; si no cambió, se excluye (era una URL).
        const resto = {};
        Object.entries(datos).forEach(([campo, valor]) => {
          if (CAMPOS_IMAGEN.includes(campo) && valor !== null) return;
          resto[campo] = valor;
        });
        body = JSON.stringify(resto);
        headers = authHeaders({ 'Content-Type': 'application/json' });
      }

      const response = await fetch(API_CIFRA.configuracion.admin, {
        method: 'PUT',
        headers,
        body,
      });
      const data = await manejarRespuesta(response);
      return { success: true, configuracion: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, obtenerConfiguracion, actualizarConfiguracion };
};

export default useConfiguracionAdmin;
